import React, { forwardRef } from "react";
import { bool, func, node, object, oneOf, string } from "prop-types";

import componentSizes from "../../constants/componentSizes";
import Field from "../Field";
import DateInput from "./components/DateInput";

const DateField = forwardRef(
  (
    {
      className,
      defaultValue,
      disabled,
      error,
      helper,
      hint,
      id,
      inputOptions,
      isValid,
      label,
      max,
      min,
      name,
      onChange,
      required,
      size,
      value,
      ...passThroughProps
    },
    ref
  ) => (
    <Field
      ref={ref}
      className={className}
      error={error}
      helper={helper}
      hint={hint}
      id={id}
      isValid={isValid}
      label={label}
      {...passThroughProps}
    >
      <DateInput
        defaultValue={defaultValue}
        disabled={disabled}
        max={max}
        min={min}
        name={name}
        onChange={onChange}
        required={required}
        size={size}
        value={value}
        {...inputOptions}
      />
    </Field>
  )
);

DateField.propTypes = {
  /**
   * Used to extend the component using `styled-components`. The `className` should be added
   * to the outermost component returned by `DateField`. Defaults to an empty string
   */
  className: string,
  /**
   * `defaultValue` is used to provision an uncontrolled component
   * with an initial value. This prop should be passed to the underlying
   * `DateInput` component. The default value is an empty string
   */
  // eslint-disable-next-line react/require-default-props
  defaultValue: string,
  /**
   * When `disabled` is set to `true`, users cannot interact with `DateInput`
   * and disabled styling is applied. Defaults to `false`.
   * This prop is passed down to the `DateInput` component
   */
  disabled: bool,
  /**
   * Represents the `error` message rendered by `Field` when the `isValid` prop is set to `false`
   */
  error: node,
  /**
   * Represents `helper` message rendered by `Field` when the `isValid` prop is set to `true`.
   * Defaults to an empty string
   */
  helper: node,
  hint: string.isRequired,
  /**
   * Used by `Field` internally to generate HTML attributes that support accessibility
   */
  id: string.isRequired,
  /**
   * Determines whether `error` message is shown in Field component, and adds error styling
   * to the underlying `DateInput` input when `true`. Defaults to `false`
   */
  isValid: bool,
  /**
   * Used to pass any additional props to the `DateInput` component
   */
  // eslint-disable-next-line react/forbid-prop-types
  inputOptions: object,
  /**
   * The label associated with the underlying `DateInput` component
   */
  label: string.isRequired,
  /**
   * Used to set a maximum value in the underlying `DateInput` component.
   * Must be a string representing a date in `YYYY-MM-DD` format, or empty.
   * Defaults to an empty string. This prop is passed down
   * to the `DateInput` component
   */
  max: string,
  /**
   * Used to set a minimum value in the underlying `DateInput` component.
   * Must be a string representing a date in `YYYY-MM-DD` format, or empty.
   * Defaults to an empty string. This prop is passed down
   * to the `DateInput` component
   */
  min: string,
  // eslint-disable-next-line react/require-default-props
  name: string,
  /**
   * Function called when the value of the underlying `DateInput`
   * component changes. Defaults to empty function `() => {}`.
   * This prop is passed down to the `DateInput` component
   */
  onChange: func,
  /**
   * When `required` is set to true, the HTML required attribute is added
   * to the underlying select input in the `DateInput` component.
   * Defaults to `false`
   */
  required: bool,
  /**
   * One of `componentSizes.SM`, `componentSizes.MD`, `componentSizes.LG`.
   * Determines the size of the `DateInput` component.
   * Defaults to `componentSizes.MD`.
   * This prop is passed down to the `DateInput` component
   */
  size: oneOf([componentSizes.SM, componentSizes.MD, componentSizes.LG]),
  /**
   * `value` is used to provision a value to a controlled component.
   * This prop should be passed down to the `DateInput` component
   */
  // eslint-disable-next-line react/require-default-props
  value: string
};

DateField.defaultProps = {
  className: "",
  disabled: false,
  error: null,
  helper: "",
  inputOptions: {},
  isValid: true,
  max: "",
  min: "",
  onChange: () => {},
  required: false,
  size: componentSizes.MD
};

DateField.displayName = "DateField";

export default DateField;
